import { useCallback } from "react";
import { useLocalStorageState } from "@/shared/hooks/useLocalStorageState";
import type { Section } from "./types";

/** Section kinds the user can save — visual / data components only (tables and
 *  charts/drawings). Textual answers, callouts, KPI rows and plain lists are
 *  excluded per the 2026-05-31 product decision. */
export const LIKEABLE_KINDS: ReadonlySet<Section["kind"]> = new Set([
  "table",
  "stat_table",
  "chart",
  "sparkline",
  "bar_list",
  "tracker",
  "bullet",
  "tier_bar",
  "store_progress",
]);

export function isLikeable(section: Section): boolean {
  return LIKEABLE_KINDS.has(section.kind);
}

export interface SavedComponent {
  /** Stable content signature — dedup + toggle key. */
  id: string;
  section: Section;
  /** The question that produced the component (shown as board context). */
  panelQuestion: string;
  savedAt: number;
}

/** Stable, synchronous content signature so identical components dedupe and
 *  toggle correctly (sections carry no id of their own). djb2 over the JSON. */
export function sectionSignature(section: Section): string {
  const json = JSON.stringify(section);
  let h = 5381;
  for (let i = 0; i < json.length; i++) {
    h = ((h << 5) + h) ^ json.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

export interface SavedComponentsApi {
  saved: SavedComponent[];
  isSaved: (section: Section) => boolean;
  toggle: (section: Section, panelQuestion: string) => void;
  remove: (id: string) => void;
  restore: (item: SavedComponent) => void;
}

/** localStorage-backed saved-components store, scoped per client/tenant. */
export function useSavedComponentsStore(clientId: string): SavedComponentsApi {
  const [saved, setSaved] = useLocalStorageState<SavedComponent[]>(
    `saved-components:${clientId}`,
    [],
  );

  const isSaved = useCallback(
    (section: Section) => {
      const id = sectionSignature(section);
      return saved.some((s) => s.id === id);
    },
    [saved],
  );

  const toggle = useCallback(
    (section: Section, panelQuestion: string) => {
      const id = sectionSignature(section);
      setSaved((prev) =>
        prev.some((s) => s.id === id)
          ? prev.filter((s) => s.id !== id)
          : [...prev, { id, section, panelQuestion, savedAt: Date.now() }],
      );
    },
    [setSaved],
  );

  const remove = useCallback(
    (id: string) => setSaved((prev) => prev.filter((s) => s.id !== id)),
    [setSaved],
  );

  const restore = useCallback(
    (item: SavedComponent) =>
      setSaved((prev) => (prev.some((s) => s.id === item.id) ? prev : [...prev, item])),
    [setSaved],
  );

  return { saved, isSaved, toggle, remove, restore };
}
